import { SamFilter, SamFilterableField, SamFilterAction, SamFilterSource, SamFilterState, SamFilterType, samQuickFilter, SamValue } from "../filterTypes";

export type SamValueGetter = <F extends SamFilterableField>(field: F | typeof samQuickFilter) => SamValue | null | undefined;
export type SamFilterActive = <T extends SamFilter>(filter: T) => boolean;

export const applyFilterState = <T extends SamFilter>(
  filterState: SamFilterState<T>,
  filterTypes: SamFilterType[],
  valueGetter: SamValueGetter,
  filterActive?: SamFilterActive,
): boolean => {
  const filteredState = filterByTypes(filterState, filterTypes);
  return filteredState.length > 0 && filteredState.every(([, filters]) => applyFilters(filters, valueGetter, filterActive));
};

export const getFilterSources = <T extends SamFilter>(
  filterState: SamFilterState<T>,
  filterTypes: SamFilterType[],
): Readonly<[SamFilterAction, SamFilterSource]>[] => {
  return filterByTypes(filterState, filterTypes)
    .flatMap(([, filters]) => filters)
    .map(({ action, source }) => [action, source] as const);
};

const filterByTypes = <T extends SamFilter>(
  filterState: SamFilterState<T>,
  filterTypes: SamFilterType[],
): [string, T[]][] => {
  return Object.entries(filterState).filter(([filterType]) => filterTypes.includes(filterType as SamFilterType));
};

const applyFilters = <T extends SamFilter>(filters: T[], valueGetter: SamValueGetter, filterActive?: SamFilterActive): boolean => {
  const filteredFilters = filterActive ? filters.filter((filter) => filterActive(filter)) : filters;

  return filteredFilters.length === 0 || filteredFilters.reduce(
    (acc, filter, index) => {
      switch (filter.action) {
        case "EXCLUDE":
          return acc && !applyFilter(filter, valueGetter);
        case "INCLUDE":
          return index !== 0
            ? acc || applyFilter(filter, valueGetter)
            : applyFilter(filter, valueGetter);
        case "INTERSECT":
          return acc && applyFilter(filter, valueGetter);
      }

      console.error("Filter action could not be evaluated: ", filter.action);
      return acc;
    },
    true,
  );
};

const applyFilter = <T extends SamFilter>(filter: T, valueGetter: SamValueGetter): boolean => {
  const value = valueGetter(filter.field);
  switch (filter.operator) {
    case "==":
      return value === filter.value;
    case "!=":
      return value !== filter.value;
    case "in": {
      const inclFilterValue = filter.value as Includeable;
      if (typeof inclFilterValue.includes === "function") {
        return inclFilterValue.includes(value);
      }
      break;
    }
    case "!in": {
      const inclFilterValue = filter.value as Includeable;
      if (typeof inclFilterValue.includes === "function") {
        return !inclFilterValue.includes(value);
      }
      break;
    }
    case "contains": {
      // null values can not contain anything
      if (!notNull(value)) {
        return false;
      }
      const inclValue = value as Includeable;
      if (typeof inclValue.includes === "function") {
        return inclValue.includes(filter.value);
      }
      break;
    }
    case "!contains": {
      // null values do not contain anything
      if (!notNull(value)) {
        return true;
      }
      const inclValue = value as Includeable;
      if (typeof inclValue.includes === "function") {
        return !inclValue.includes(filter.value);
      }
      break;
    }
    case "<":
      return notNull(value) && value < filter.value;
    case "<=":
      return notNull(value) && value <= filter.value;
    case ">=":
      return notNull(value) && value >= filter.value;
    case ">":
      return notNull(value) && value > filter.value;
    case "regex_match": {
      // non-string values can not be matched
      if (typeof value !== "string") {
        return false;
      }
      // a match filter must always be [pattern, flags?]
      if (!Array.isArray(filter.value)) {
        return false;
      }
      const [pattern, flags] = filter.value;
      if (typeof pattern !== "string" || (typeof flags !== "undefined" && typeof flags !== "string")) {
        return false;
      }
      return new RegExp(pattern, flags).test(value);
    }
    case "between": {
      if (!Array.isArray(filter.value) || filter.value.length !== 2) {
        return false;
      }
      if (Array.isArray(value) && value.length === 2) {
        return (filter.value[0] <= value[0] && filter.value[1] >= value[0])
          || (filter.value[0] <= value[1] && filter.value[1] >= value[1])
          || (filter.value[0] > value[0] && filter.value[1] < value[1]);
      } else if (value && !Array.isArray(value)) {
        return filter.value[0] <= value && filter.value[1] >= value;
      }
      return false;
    }
  }

  console.error("Filter could not be evaluated: ", filter, value);
  return false;
};

interface Includeable {
  includes?(search: SamValue | null | undefined): boolean;
}

const notNull = <T>(value: T | null | undefined): value is T => {
  return value !== null && value !== undefined;
};
